/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Carries data that was fetched at build time (prerender) or hydrated from
 * `window.__PRERENDER_DATA__` so page components can render their initial
 * markup without waiting on a fetch. After hydration, normal client navigation
 * triggers fetches as usual.
 */

import React, { createContext, useContext } from 'react';
import type { EREProvider } from '../types';
import type { PageStructureFile } from '../types/pageStructure';
import type { PublicAuthor } from '../components/AuthorAvatar';
import type { ChargerBrandDetail, ChargerBrandIndexRow, MidChargersData } from '../lib/midChargers';
import type { InstallerOfferPayload } from '../lib/installerOffer';
import type {
  NeaListStatus,
  NeaOverviewRow,
  ProviderProfilePayload,
  PublicProviderProfile,
} from '../types/api/providerPayloads';

// The NEa-list dates: ONE definition with the payloads that carry them
// (src/types/api/providerPayloads.ts), under the name the pages import.
export type { NeaListStatus };

/**
 * One row of GET /api/nea-providers as a page holds it — seeded by the bake or
 * fetched: the builder's row (NeaOverviewRow, src/types/api/providerPayloads.ts)
 * with all but the identity optional, so a row from before a field existed
 * still renders, as a register row without facts.
 */
type OverviewIdentity = 'id' | 'name' | 'logoUrl' | 'providerUrl';
export type NeaOverviewSeedRow = Pick<NeaOverviewRow, OverviewIdentity> &
  Partial<Omit<NeaOverviewRow, OverviewIdentity>>;

/**
 * GET /api/provider-profile/:slug as a page holds it — seeded by the bake or
 * fetched: the builder's payload (ProviderProfilePayload) with everything past
 * the identity optional, the profile's photo position and shape and its
 * timestamps included, so a payload from before a field existed still renders
 * without it.
 */
type ProfileIdentity = 'providerId' | 'slug' | 'name' | 'providerUrl';
type ProfileLater = 'photoFocus' | 'photoShape' | 'createdAt' | 'updatedAt';
export type ProviderProfileSeed = Pick<ProviderProfilePayload, ProfileIdentity> & {
  profile: Omit<PublicProviderProfile, ProfileLater> &
    Partial<Pick<PublicProviderProfile, ProfileLater>>;
} & Partial<Omit<ProviderProfilePayload, ProfileIdentity | 'profile'>>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ArticleSummary = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ArticleDetail = any;

/** One row of GET /api/begrippen — begripIndexRows in src/lib/begrippen.js. */
export interface BegripIndexRow {
  id: string;
  slug: string;
  group: string;
  term: string;
  definition: string;
  hasEnglish: boolean;
}

/** GET /api/begrippen/:slug — begripDetailPayload in src/lib/begrippen.js. */
export interface BegripDetail {
  id: string;
  slug: string;
  slugByLang: { nl: string; en: string };
  hasEnglish: boolean;
  group: string;
  term: string;
  definition: string;
  // The same block schema as an article (src/pages/admin/articleTypes.ts).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  blocks: any[];
  seoTitle: string;
  seoDescription: string;
  createdAt: string;
  updatedAt: string;
  related: Array<{ id: string; slug: string; term: string }>;
}

/**
 * Compact, name-only matchup card seeded into prerendered provider-profile
 * pages so the "compare with" hub links are baked into the static HTML (SEO
 * link equity) without inlining the full providers payload on every profile.
 */
export interface ProfileMatchupCard {
  matchup: string;
  a: { slug: string; name: string };
  b: { slug: string; name: string };
}

/**
 * When the providers file was last written (ISO 8601; `null` = there is no
 * file to date): the comparer's "Aanbod bijgewerkt op". Read once per bake by
 * src/lib/providersMeta.js, the reader GET /api/providers/meta also uses.
 */
export interface ProvidersMeta {
  updatedAt: string | null;
}

export interface PrerenderData {
  lang: 'nl' | 'en';
  url: string;
  providers?: EREProvider[];
  /**
   * The date behind `providers`, seeded on the same routes
   * (scripts/prerender.mjs). A route the bake does not seed gets it from
   * GET /api/providers/meta instead.
   */
  providersMeta?: ProvidersMeta;
  articlesIndex?: ArticleSummary[];
  articleDetail?: ArticleDetail | null;
  /**
   * /begrippen — the list rows the cards and the DefinedTermSet render from.
   * Seeded on the list route only; a term page carries its own `related`.
   */
  begrippenIndex?: BegripIndexRow[];
  /** /begrippen/:slug — the term the route is about. */
  begripDetail?: BegripDetail | null;
  neaProviders?: NeaOverviewSeedRow[];
  /**
   * The dated ERE price series for /ere-prijs, oldest first. Seeded so the
   * chart and the table are in the baked HTML — the page exists to be read
   * by crawlers and answer engines, which never run the fetch.
   */
  erePriceEntries?: Array<{
    date: string;
    price: number;
    low: number;
    high: number;
    source?: string;
    sourceUrl?: string;
    note?: string;
  }>;
  /**
   * Per-page entries lists. Seeded by the prerender script so SSR-rendered
   * pages walk the real entries[] on first paint instead of falling back to
   * the legacy section ordering. Optional — pages tolerate it being missing
   * and CSR-fetch as a fallback.
   */
  pageStructure?: PageStructureFile;
  /**
   * Per-route provider profile payload — same shape as /api/provider-profile/:slug.
   * Seeded so /providers/:slug renders the full profile (description, team
   * message, contracts) into the HTML body for crawlers + AI agents instead of
   * showing a loading spinner until the client-side fetch resolves.
   */
  providerProfile?: ProviderProfileSeed;
  /**
   * Compact matchup cards for the provider-profile "compare with" hub. Seeded
   * only on /providers/:slug routes when the provider is in the canonical
   * set; CSR-derived from /api/providers otherwise.
   */
  profileMatchups?: ProfileMatchupCard[];
  /**
   * Public admin profiles (same shape as /api/authors, already filtered on
   * showOnPublicSite and sorted by display name). Seeded on the /about route
   * only, for the team section.
   */
  authors?: PublicAuthor[];
  /** /auteur/:slug — the author the route is about. */
  authorDetail?: PublicAuthor | null;
  /** /providers — see {@link NeaListStatus}. */
  neaListStatus?: NeaListStatus | null;
  /**
   * /partner only: the facts-row counts of the aanbiedersoverzicht
   * (src/lib/providerOverview.js overviewCounts), so the value page's
   * "{total} partijen, {compared} in de vergelijker" line is baked rather than
   * typed. The client refetches after hydration; a later update is a state
   * change, not a mismatch.
   */
  neaOverviewCounts?: NeaOverviewCounts;
  /**
   * The MID-charger registry on /mid-check, the exact payload GET
   * /api/mid-chargers serves (src/lib/chargerCatalogRecords.js midChargersPayload):
   * the public register rows (the register's fields plus `specs`), the stamp,
   * and `brandPages` (brand name → slug of its published brand page). Seeded
   * on every /mid-check bake since 2026-10-01 — the browser bundle no longer
   * carries the seed (src/lib/useMidChargers.ts, scripts/prerender.mjs).
   */
  midChargers?: MidChargersData;
  /**
   * /laadpalen — the brand hub: exactly what GET /api/charger-brands serves
   * (src/lib/chargerCatalogRecords.js chargerBrandIndexRows + chargerBrandsUpdatedAt).
   * Seeded on the hub route only. Read by useChargerBrands
   * (src/lib/useChargerBrands.ts).
   */
  chargerBrands?: ChargerBrandsHubData;
  /**
   * /laadpalen/:slug — the brand the route is about: exactly what GET
   * /api/charger-brands/:slug serves (chargerBrandDetailPayload). The payload
   * holds both languages' texts, so one object serves both bakes of a brand.
   * Read by useChargerBrand.
   */
  chargerBrand?: ChargerBrandDetail | null;
  /** /geen-laadpaal — exactly what GET /api/installer-offer serves; read by useInstallerOffer. */
  installerOffer?: InstallerOfferPayload;
}

/** GET /api/charger-brands — the hub rows in brand order, and when anything on them last changed. */
export interface ChargerBrandsHubData {
  brands: ChargerBrandIndexRow[];
  /** ISO 8601, or `''` when nothing on the hub is dated. */
  updated_at: string;
}

export interface NeaOverviewCounts {
  total: number;
  inComparison: number;
  neaListed: number;
}

const PrerenderContext = createContext<PrerenderData | null>(null);

export const PrerenderProvider: React.FC<{
  value: PrerenderData | null;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <PrerenderContext.Provider value={value}>{children}</PrerenderContext.Provider>
);

export function usePrerender(): PrerenderData | null {
  return useContext(PrerenderContext);
}
